import { ViewIds } from '@ardoq/api-types';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import type { FunctionComponent } from 'react';
import {
  buildViewpointModeViewModelSourceData,
  getInitialViewpointModeSourceDataState,
  ViewpointModeTimelineSourceDataArgs,
  warmTimelineFilterCache,
} from './sourceData/buildViewpointModeViewModelSourceData';
import {
  TimelineRenderingLayerViewSettingsProps,
  ViewModelSourceData,
  buildSettingsControlsForViewpointMode,
  defaultTimelineViewCommands,
} from '@ardoq/timeline2024';
import type { TimelineSettingsControlsDependencies } from '@ardoq/timeline2024';
import { ViewpointModeTimelineSourceDataLayer } from './ViewpointModeTimelineSourceDataLayer';
import { shallowEqual } from 'utils/objectUtils';
import { updateViewProperties } from '@ardoq/view-settings';
import { ViewLegendComponent } from './ViewLegendComponent';

import {
  getTimelineViewSettings$,
  getUiOnlyViewSettings$,
} from './viewModel/viewSettingsUtils';
import {
  combineLatest,
  debounceTime,
  defer,
  distinctUntilChanged,
  map,
  Observable,
  scan,
} from 'rxjs';

import { errorInfoBoxLabels$ } from './errorInfoBoxLabels$';
import { startTimelineTracking } from './performanceTrackingUtils';
import { isGraphRelevant } from './sourceData/isGraphRelevant';
import type {
  HasDataWorkToken,
  ViewStatusReporter,
} from 'tabview/activeViewsStatus/types';
import {
  createViewStatusReporter,
  registerWhileSubscribed,
  reportDataWorkStarted,
  withDataWork,
} from 'tabview/activeViewsStatus/viewStatusReporter';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { Box } from '@ardoq/layout';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { context$ } from 'streams/context/context$';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { graphGroupingRules$ } from 'streams/grouping/graphGroupingRules$';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import { orgUsersById$ } from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
const VIEW_ID = ViewIds.TIMELINE;
const VIEW_INSTANCE_ID = toViewpointModeMainViewInstanceId(VIEW_ID);

const updateViewSettingsDataForViewpointMode = (
  settingsData: TimelineSettingsControlsDependencies
) => {
  dispatchAction(
    updateViewProperties({
      viewId: VIEW_ID,
      viewProperties: settingsData,
    })
  );
};

const viewpointModeSettingsControls = {
  getSettingsControlsData: buildSettingsControlsForViewpointMode,
  publishSettingsControlsData: updateViewSettingsDataForViewpointMode,
};

type SourceDataLayerStreamedProps = TimelineRenderingLayerViewSettingsProps &
  HasDataWorkToken & {
    sourceData: ViewModelSourceData;
    viewStatusReporter: ViewStatusReporter;
  };

type SourceDataState = {
  sourceDataArgs: ViewpointModeTimelineSourceDataArgs | null;
  sourceData: ViewModelSourceData;
};

const rebuildSourceData = (
  sourceData: ViewModelSourceData,
  sourceDataArgs: ViewpointModeTimelineSourceDataArgs
): ViewModelSourceData => {
  warmTimelineFilterCache(sourceDataArgs.loadedGraph);
  return buildViewpointModeViewModelSourceData(sourceData, {
    ...sourceDataArgs,
    trackingId: startTimelineTracking(),
  });
};

const updateSourceData = (
  { sourceDataArgs: previousSourceDataArgs, sourceData }: SourceDataState,
  sourceDataArgs: ViewpointModeTimelineSourceDataArgs
): SourceDataState => {
  if (isGraphRelevant(previousSourceDataArgs, sourceDataArgs)) {
    return {
      sourceDataArgs,
      sourceData: rebuildSourceData(sourceData, sourceDataArgs),
    };
  }
  return {
    sourceDataArgs,
    sourceData: {
      ...sourceData,
      sourceDataViewSettings: sourceDataArgs.viewSettings,
    },
  };
};

const toSourceData = ({
  sourceData,
}: SourceDataState): Pick<SourceDataState, 'sourceData'> => ({ sourceData });

const viewStatusReporter = createViewStatusReporter(VIEW_INSTANCE_ID);

const viewSettingsWithChanges$ = getViewpointModeViewSettingsWithChanges$(
  VIEW_ID,
  VIEW_INSTANCE_ID
);

const sourceDataArgs$ = combineLatest({
  context: context$,
  viewSettings: getTimelineViewSettings$(viewSettingsWithChanges$),
  loadedGraph: loadedGraph$,
  locale: locale$,
  i18nLocale: i18nLocale$,
  orgUsersById: orgUsersById$,
  errorInfoBoxLabels: errorInfoBoxLabels$,
  groupingRules: graphGroupingRules$,
  formattingAndLabelRules: formattingAndLabelRules$.pipe(
    map(({ rules }) => rules)
  ),
}).pipe(
  distinctUntilChanged(shallowEqual),
  reportDataWorkStarted(viewStatusReporter, 'reset'),
  debounceTime(DEFAULT_DEBOUNCE_TIME)
);

// The initial state holds stateful callbacks, so every mount gets its own.
const sourceData$: Observable<
  Pick<SourceDataState, 'sourceData'> & HasDataWorkToken
> = defer(() =>
  sourceDataArgs$.pipe(
    scan(updateSourceData, {
      sourceDataArgs: null,
      sourceData: getInitialViewpointModeSourceDataState(VIEW_INSTANCE_ID),
    }),
    map(toSourceData),
    withDataWork(viewStatusReporter, 'source')
  )
);

const sourceDataLayerProps$: Observable<SourceDataLayerStreamedProps> =
  combineLatest([
    sourceData$,
    getUiOnlyViewSettings$(viewSettingsWithChanges$),
  ]).pipe(
    map(([sourceDataWithWork, uiOnlyViewSettings]) => ({
      ...sourceDataWithWork,
      uiOnlyViewSettings,
      viewStatusReporter,
    })),
    registerWhileSubscribed(viewStatusReporter)
  );

const ConnectedSourceDataLayer = connect(
  ViewpointModeTimelineSourceDataLayer,
  sourceDataLayerProps$
);

const ViewpointModeTimelineView: FunctionComponent = () => {
  return (
    <Box className="prevent-context-change" width="full" height="full">
      <ConnectedSourceDataLayer
        viewInstanceId={VIEW_INSTANCE_ID}
        settingsControlsHandlers={viewpointModeSettingsControls}
        commands={defaultTimelineViewCommands}
        ViewLegendComponent={ViewLegendComponent}
      />
    </Box>
  );
};

export { ViewpointModeTimelineView };
